import { useMemo } from 'react'

import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { Finalidade, Purposes } from 'common/types'
import { TUserType } from 'contexts/app-context/types'

import { useQuery } from '@tanstack/react-query'

import { getPurposesQueryOptions } from './queries/getPurposesQueryOptions'

function isPJ(userType: TUserType) {
  return userType === 'PJ'
}

export const useGetPurposes = () => {
  const { privacyCenterContent, userAccountType, isUSBranch, userCountry, userSelectedEntity } =
    useAppContext()

  const productMenu =
    privacyCenterContent?.components?.manageNotifications?.content.productsSection.productMenu

  const endpoint = privacyCenterContent?.endpoints?.preferences?.getPreferences

  const {
    data,
    status: purposesStatus,
    isError: isPurposesError,
  } = useQuery(
    getPurposesQueryOptions({
      endpoint,
      userCountry,
      userSelectedEntity,
      userAccountType,
      isUSBranch,
    }),
  )

  const purposes = useMemo(() => {
    if (!data || !userAccountType) return []

    const isPj = isPJ(userAccountType)

    if (isPj && productMenu) {
      const response = data.finalidades

      const productIds = Object.keys(productMenu) as (keyof typeof productMenu)[]

      const productList = productIds
        .map((productId) => {
          const { subProducts } = productMenu[productId]

          return {
            id: productId,
            subProducts,
          }
        })
        .filter((products) => Boolean(products.subProducts))
        .map((products) => ({
          ...products,
          subProducts: products.subProducts as string[],
        }))

      const filteredGoals = productList.map((product) => {
        const goals: Finalidade[] = response.filter((finalidade) =>
          product.subProducts.includes(finalidade.id),
        )

        const metadados = goals.reduce((prev, curr) => {
          const metadatas = prev.metadados.map((metadata) => {
            const currMetadata = curr.metadados.find(
              (currMetadataAux) => currMetadataAux.chave === metadata.chave,
            )

            const metadataValue = !currMetadata
              ? metadata.valor
              : currMetadata.valor && metadata.valor
            return { chave: metadata.chave, valor: metadataValue }
          })

          return { id: 'PREFERENCES', metadados: metadatas, subProduto: [] }
        })

        return {
          id: product.id,
          metadados: metadados.metadados,
          subProduto: [],
        }
      })

      return filteredGoals
    }

    return (data as Purposes)?.finalidades
  }, [data, productMenu, userAccountType])

  return {
    purposes,
    purposesStatus,
    isPurposesError,
    isPurposesLoading: purposesStatus === 'pending',
  }
}
